import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { fetchHistoricoPublicacao, publicacaoKeys } from '@/services/publicacaoService';
import { parseApiError } from '@/utils/apiError';
import type { IHistoricoPublicacao } from '@/types/publicacao';

export const Route = createFileRoute('/_authenticated/historico-publicacao/')({
  component: HistoricoPublicacao,
});

function getStatusColor(status: string | null | undefined): string {
  const s = (status || '').toUpperCase();
  if (s === 'SUCESSO') return '#15803d';
  if (s === 'ERRO' || s === 'FALHA') return '#b91c1c';
  return '#64748b';
}

function formatarData(dataString: string | null | undefined): string {
  if (!dataString) return '-';
  return new Date(dataString).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function HistoricoPublicacao() {
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const filtros = { search: debouncedSearch, page, page_size: pageSize };

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: publicacaoKeys.historico(filtros),
    queryFn: () => fetchHistoricoPublicacao(filtros),
  });

  const historico: IHistoricoPublicacao[] = data?.items ?? [];
  const totalPages = data?.total_pages ?? 0;
  const totalCount = data?.total_count ?? 0;

  if (isLoading) {
    return <div className="text-center text-on-surface-variant">Carregando histórico de publicação...</div>;
  }

  if (isError) {
    return (
      <div className="rounded-lg bg-error-container p-4 text-on-error-container">
        Erro ao carregar histórico: {parseApiError(error)}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-xl font-semibold text-on-surface">Histórico de Publicações MAPA</h2>

      <div className="flex flex-wrap items-end gap-4 rounded-lg border border-outline-variant bg-surface-container-lowest p-4">
        <div className="flex flex-col gap-1">
          <label className="text-sm text-on-surface-variant">Busca:</label>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Buscar por tipo, status ou mensagem"
            className="rounded-lg border border-outline-variant bg-surface-container-lowest px-3 py-2 text-on-surface outline-none focus:border-primary"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm text-on-surface-variant">Itens por página:</label>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            className="rounded-lg border border-outline-variant bg-surface-container-lowest px-3 py-2 text-on-surface outline-none focus:border-primary"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        <button
          onClick={() => void refetch()}
          className="rounded-lg bg-primary px-4 py-2 font-medium text-on-primary transition-opacity hover:opacity-90"
        >
          Atualizar
        </button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-outline-variant">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-outline-variant bg-surface-container text-left">
              <th className="p-3">ID</th>
              <th className="p-3">Tipo</th>
              <th className="p-3">Operação</th>
              <th className="p-3">Unidade</th>
              <th className="p-3">Mês</th>
              <th className="p-3">Data Publicação</th>
              <th className="p-3">Status</th>
              <th className="p-3">Erro</th>
              <th className="p-3">Criado em</th>
            </tr>
          </thead>
          <tbody>
            {historico.map((item) => (
              <tr
                key={item.id}
                className="border-b border-outline-variant last:border-0"
              >
                <td className="p-3">{item.id_mapa}</td>
                <td className="p-3">
                  <span className="rounded-full bg-primary px-2.5 py-1 text-xs font-medium text-on-primary">
                    {item.tipo_publicacao}
                  </span>
                </td>
                <td className="p-3">
                  {item.operacao === 'excluir' ? 'Excluir' : 'Publicar'}
                </td>
                <td className="p-3">{item.unidade_nome ?? '-'}</td>
                <td className="p-3">{item.mes}</td>
                <td className="p-3">{formatarData(item.data_publicacao || item.data_fim)}</td>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-block h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: getStatusColor(item.status) }}
                    >
                    </span>
                    <span>{item.status}</span>
                  </div>
                </td>
                <td className="p-3">
                  {item.sucesso === false && item.mensagem ? (
                    <span title={item.mensagem}>
                      {item.mensagem.length > 50 ? `${item.mensagem.substring(0, 50)}...` : item.mensagem}
                    </span>
                  ) : (
                    <span className="text-on-surface-variant">-</span>
                  )}
                </td>
                <td className="p-3">{formatarData(item.data_inicio)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {historico.length === 0 && (
          <div className="p-6 text-center text-on-surface-variant">
            Nenhum registro de publicação encontrado para os filtros selecionados.
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={() => setPage((p) => p - 1)}
            disabled={page <= 1}
            className="rounded-lg border border-outline-variant px-4 py-2 text-on-surface disabled:opacity-40"
          >
            Anterior
          </button>

          <span className="text-sm text-on-surface-variant">
            Página {page} de {totalPages} ({totalCount} registros)
          </span>

          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={page >= totalPages}
            className="rounded-lg border border-outline-variant px-4 py-2 text-on-surface disabled:opacity-40"
          >
            Próxima
          </button>
        </div>
      )}
    </div>
  );
}
